// Success-finalisation modal: the manager picks the winning bid and
// finalises the request to SUCCESS as a pair. Refactored in #1164 — the
// modal now mirrors CloseRequestModal's flow (pickWinner + transition).
// When a winner is already preselected (either picked in IN_WORK or
// pre-pinned), the matching row is highlighted on open and confirm
// fires only `success` without a redundant `pickWinner`.
//
// @aiInvariant Confirm is disabled until a bid is picked. Server-side
//   rejections keep the modal open and surface inline via
//   `submitError` so the user can recover.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestStatus } from '@magimp/shared';
import { Button, Icon, Modal } from '../../../../ui/index.js';
import { useRequestsStore } from '../../store.js';
import { usePickWinner, useTransition } from '../../queries.js';
import { errorMessage } from '../../../../api/errors.js';
import { carrierMarkText } from '../../carrierMark.js';
import { formatDateTime, formatMoney } from '../../format.js';
import type { CarrierBid, CarrierBidQuote, RequestView, UserRef } from '../../types.js';

export interface SuccessRequestModalProps {
  request: RequestView;
  me: UserRef;
}

interface Row {
  carrier: CarrierBid;
  bid: CarrierBidQuote;
}

function bidValidUntil(q: CarrierBidQuote): number {
  return q.quotedAt + q.validityDays * 24 * 3600 * 1000;
}

function isExpired(q: CarrierBidQuote, now: number): boolean {
  return now >= bidValidUntil(q);
}

export function SuccessRequestModal({ request }: SuccessRequestModalProps) {
  const { t } = useTranslation(['requests', 'common']);
  const setModal = useRequestsStore((s) => s.setModal);
  const transitionMutation = useTransition(request.id);
  const pickWinnerMutation = usePickWinner(request.id);
  const onClose = () => setModal(null);
  // INVARIANT (#703): only REQUEST-scope (primary) bids are valid winners.
  //   Overrides cover a sub-resource and would be ambiguous as a
  //   request-level winner. Backend re-checks at COMMIT time.
  const rows: Row[] = [];
  for (const carrier of request.carriers) {
    if (carrier.state !== 'answered') continue;
    for (const bid of carrier.bids) {
      if (bid.scope !== 'REQUEST') continue;
      rows.push({ carrier, bid });
    }
  }
  const [picked, setPicked] = useState<string | null>(request.winnerBidId);
  // INVARIANT (#1836): a POSTPONED row preserves its winner; reviving it to
  //   SUCCESS must REUSE that winner (the backend rejects a re-pick). Lock the
  //   selection so the manager cannot switch bids — `picked` stays equal to
  //   `request.winnerBidId`, so `onConfirm` skips `pickWinner` and the success
  //   commit reuses the stored winner. REFUSED revive (winner null) and a
  //   POSTPONED row without a winner keep the full picker; PROCESSED unchanged.
  const lockToStoredWinner =
    request.winnerBidId != null && request.status === RequestStatus.POSTPONED;
  const winnerRow = rows.find((r) => r.bid.id === picked) ?? null;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const now = Date.now();

  async function onConfirm(): Promise<void> {
    if (!picked) return;
    setSubmitError(null);
    try {
      // #1164: commit `pickWinner` + `success` as a pair. The first
      //   call is skipped when the selection matches the stored winner
      //   (no redundant write, no audit noise).
      if (request.winnerBidId !== picked) {
        await pickWinnerMutation.mutateAsync(picked);
      }
      await transitionMutation.mutateAsync({ key: 'success', body: { bidId: picked } });
      setModal(null);
    } catch (err) {
      setSubmitError(errorMessage(err));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('requests:successModal.title', { id: request.id })}
      width={520}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            kind="primary"
            icon="check-check"
            disabled={!picked || transitionMutation.isPending || pickWinnerMutation.isPending}
            onClick={() => {
              void onConfirm();
            }}
          >
            {t('requests:successModal.confirm')}
          </Button>
        </>
      }
    >
      {rows.length > 0 ? (
        <>
          <div style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 10 }}>
            {winnerRow
              ? t('requests:successModal.winnerText', { name: winnerRow.carrier.carrier.name })
              : t('requests:closeModal.pickWinner')}
          </div>
          {winnerRow && isExpired(winnerRow.bid, now) && (
            <div
              role="alert"
              style={{
                marginBottom: 12,
                padding: 10,
                background: 'var(--status-warn-bg)',
                borderRadius: 'var(--r-2)',
                display: 'flex',
                gap: 8,
                fontSize: 12,
                color: 'var(--status-warn-fg)',
              }}
            >
              <Icon name="timer-off" size={14} style={{ flex: 'none', marginTop: 1 }} />
              <span>
                {t('requests:successModal.expiredWarning', {
                  date: formatDateTime(bidValidUntil(winnerRow.bid)),
                })}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map(({ carrier, bid }) => {
              const selected = picked === bid.id;
              return (
                <button
                  key={bid.id}
                  type="button"
                  data-testid={`success-modal-bid-row-${bid.id}`}
                  disabled={lockToStoredWinner}
                  onClick={lockToStoredWinner ? undefined : () => setPicked(bid.id)}
                  style={{
                    border: `1px solid ${selected ? 'var(--accent-1)' : 'var(--border-1)'}`,
                    borderRadius: 'var(--r-2)',
                    padding: 12,
                    background: selected ? 'var(--accent-1-subtle)' : 'var(--bg-0)',
                    color: 'var(--fg-1)',
                    textAlign: 'left',
                    cursor: lockToStoredWinner ? 'default' : 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Icon name="crown" size={14} style={{ color: 'var(--accent-1-press)' }} />
                    <span style={{ fontWeight: 600 }}>{carrier.carrier.name}</span>
                    <span
                      aria-hidden="true"
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--fg-3)',
                        padding: '2px 5px',
                        background: 'var(--bg-inset)',
                        borderRadius: 3,
                      }}
                    >
                      {carrierMarkText(carrier.carrier.name, carrier.carrier.code)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 24, fontSize: 13, flexWrap: 'wrap' }}>
                    <KV
                      label={t('requests:carriers.columns.price')}
                      value={`${formatMoney(bid.pricePerUnit, bid.currency)}/${bid.unit}`}
                    />
                    <div style={{ flex: 1, minWidth: 120 }}>
                      <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>
                        {t('requests:carriers.columns.comment')}
                      </span>
                      <div style={{ fontSize: 12 }}>{bid.comment || '-'}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div
          style={{
            padding: 12,
            background: 'var(--status-warn-bg)',
            borderRadius: 'var(--r-2)',
            display: 'flex',
            gap: 10,
          }}
        >
          <Icon
            name="alert-triangle"
            size={16}
            style={{ color: 'var(--status-warn-fg)', flex: 'none', marginTop: 1 }}
          />
          <div style={{ fontSize: 13, color: 'var(--fg-1)' }}>
            {t('requests:successModal.noBids')}
          </div>
        </div>
      )}
      {submitError && (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name="alert-triangle" size={14} />
          <span>{submitError}</span>
        </div>
      )}
    </Modal>
  );
}

function KV({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div>
      <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>{label}</span>
      <div style={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{value ?? '-'}</div>
    </div>
  );
}
